/**
 * SCORING V2 DISPLAY COMPONENT
 *
 * Composant principal pour afficher les résultats du scoring unifié v2.0
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Progress,
  Button,
  Tabs,
  Alert,
  Spin,
  Statistic,
  Row,
  Col,
  Tag,
  Collapse,
  Switch,
  Divider,
  Timeline,
  message,
} from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  BarChartOutlined,
  HistoryOutlined,
  ExperimentOutlined,
} from '@ant-design/icons';

import type {
  CompleteV2ScoringResult,
  SimulationResult,
  ScoringHistory,
  EnrichmentConsents,
  RiskLevel,
} from '@/types/scoring-v2';
import { ScoringV2Service } from '@/services/ScoringV2Service';

const { Panel } = Collapse;
const { TabPane } = Tabs;

interface Props {
  applicationId: string;
  onScoreUpdate?: (score: number) => void;
}

export const ScoringV2Display: React.FC<Props> = ({ applicationId, onScoreUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [scoringResult, setScoringResult] = useState<CompleteV2ScoringResult | null>(null);
  const [simulation, setSimulation] = useState<SimulationResult | null>(null);
  const [history, setHistory] = useState<ScoringHistory | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [useEnrichment, setUseEnrichment] = useState(true);
  const [consents, setConsents] = useState<EnrichmentConsents>({
    credit_check: true,
    mobile_money: true,
    banking: true,
    microfinance: true,
  });

  /**
   * Calculer le score
   */
  const performScoring = async (forceRefresh = false) => {
    setLoading(true);
    try {
      const result = await ScoringV2Service.scoreApplication({
        application_id: applicationId,
        use_enrichment: useEnrichment,
        consents: useEnrichment ? consents : undefined,
        force_refresh: forceRefresh,
      });
      setScoringResult(result);
      if (onScoreUpdate && result.scoring) {
        onScoreUpdate(result.scoring.score_global);
      }
      message.success('Score calculé avec succès');
    } catch (error: any) {
      message.error('Erreur lors du calcul du score: ' + (error.message || 'Erreur inconnue'));
      console.error('Scoring error:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Simuler une amélioration
   */
  const performSimulation = async (improvements: any) => {
    setLoading(true);
    try {
      const result = await ScoringV2Service.simulateScenario({
        application_id: applicationId,
        scenario: improvements,
      });
      setSimulation(result);
      setActiveTab('simulation');
      message.success('Simulation effectuée');
    } catch (error: any) {
      message.error('Erreur lors de la simulation: ' + (error.message || 'Erreur inconnue'));
    } finally {
      setLoading(false);
    }
  };

  /**
   * Charger l'historique
   */
  const loadHistory = async () => {
    setLoading(true);
    try {
      const result = await ScoringV2Service.getHistory(applicationId);
      setHistory(result);
    } catch (error: any) {
      message.error('Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Obtenir la couleur du risque
   */
  const getRiskColor = (level: RiskLevel): string => {
    const colors: Record<RiskLevel, string> = {
      MINIMAL: '#52c41a',
      FAIBLE: '#73d13d',
      'MODÉRÉ': '#faad14',
      'ÉLEVÉ': '#fa8c16',
      CRITIQUE: '#f5222d',
    };
    return colors[level] || '#8c8c8c';
  };

  /**
   * Obtenir l'icône du risque
   */
  const getRiskIcon = (level: RiskLevel) => {
    const icons: Record<RiskLevel, React.ReactNode> = {
      MINIMAL: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
      FAIBLE: <CheckCircleOutlined style={{ color: '#73d13d' }} />,
      MODÉRÉ: <WarningOutlined style={{ color: '#faad14' }} />,
      ÉLEVÉ: <WarningOutlined style={{ color: '#ff7a45' }} />,
      CRITIQUE: <CloseCircleOutlined style={{ color: '#f5222d' }} />,
    };
    return icons[level];
  };

  /**
   * Rendu du score global
   */
  const renderScoreOverview = () => {
    if (!scoringResult) return null;

    const { scoring, enrichment } = scoringResult;

    return (
      <div className="score-overview">
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card>
              <Statistic
                title="Score Global"
                value={scoring.score_global}
                suffix="/100"
                precision={1}
                valueStyle={{ color: getRiskColor(scoring.niveau_risque) }}
                prefix={getRiskIcon(scoring.niveau_risque)}
              />
              <Progress
                percent={scoring.score_global}
                strokeColor={getRiskColor(scoring.niveau_risque)}
                size="small"
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card>
              <Statistic
                title="Niveau de Risque"
                value={scoring.niveau_risque}
                valueStyle={{ color: getRiskColor(scoring.niveau_risque) }}
              />
              <Tag color={getRiskColor(scoring.niveau_risque)} style={{ marginTop: 8 }}>
                {scoring.decision_recommandee}
              </Tag>
            </Card>
          </Col>
        </Row>

        {enrichment && (
          <Alert
            message="Score enrichi"
            description={`Qualité de l'enrichissement: ${enrichment.summary.enrichment_quality} | ${enrichment.summary.sources_count}/4 sources utilisées`}
            type="info"
            showIcon
            icon={<ThunderboltOutlined />}
            style={{ marginTop: 16 }}
          />
        )}

        <Divider>Facteurs de Scoring</Divider>

        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card size="small">
              <Statistic
                title="Capacité de Paiement (35%)"
                value={scoring.facteurs.capacite_paiement}
                suffix="/100"
                precision={1}
              />
              <Progress
                percent={scoring.facteurs.capacite_paiement}
                size="small"
                strokeColor="#1890ff"
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card size="small">
              <Statistic
                title="Stabilité Revenus (25%)"
                value={scoring.facteurs.stabilite_revenus}
                suffix="/100"
                precision={1}
              />
              <Progress
                percent={scoring.facteurs.stabilite_revenus}
                size="small"
                strokeColor="#52c41a"
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Historique (20%)"
                value={scoring.facteurs.historique_paiement}
                suffix="/100"
                precision={1}
              />
              <Progress
                percent={scoring.facteurs.historique_paiement}
                size="small"
                strokeColor="#faad14"
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Garanties (15%)"
                value={scoring.facteurs.garanties}
                suffix="/100"
                precision={1}
              />
              <Progress
                percent={scoring.facteurs.garanties}
                size="small"
                strokeColor="#722ed1"
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card size="small">
              <Statistic
                title="Profil Risque (5%)"
                value={scoring.facteurs.profil_risque}
                suffix="/100"
                precision={1}
              />
              <Progress
                percent={scoring.facteurs.profil_risque}
                size="small"
                strokeColor="#eb2f96"
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  };

  /**
   * Rendu des détails
   */
  const renderDetails = () => {
    if (!scoringResult) return null;

    const { scoring } = scoringResult;

    return (
      <div className="scoring-details">
        <Collapse defaultActiveKey={['explications']}>
          <Panel header="Explications" key="explications">
            <Timeline>
              {scoring.explications.map((expl, index) => (
                <Timeline.Item key={index}>{expl}</Timeline.Item>
              ))}
            </Timeline>
          </Panel>

          <Panel header="Recommandations" key="recommendations">
            {scoring.recommandations.map((rec, index) => (
              <Card key={index} size="small" style={{ marginBottom: 8 }}>
                <Row justify="space-between" align="middle">
                  <Col span={16}>
                    <strong>{rec.action}</strong>
                    <p style={{ margin: 0, color: '#8c8c8c' }}>{rec.description}</p>
                  </Col>
                  <Col span={8} style={{ textAlign: 'right' }}>
                    <Tag color={rec.priorite === 'haute' ? 'red' : rec.priorite === 'moyenne' ? 'orange' : 'blue'}>
                      {rec.priorite}
                    </Tag>
                    <div>Impact: +{rec.impact_estime} points</div>
                  </Col>
                </Row>
              </Card>
            ))}
          </Panel>

          <Panel header="Détails Techniques" key="technical">
            <Row gutter={[16, 8]}>
              <Col span={8}>
                <Statistic title="Taux d'effort" value={scoring.details.taux_effort} suffix="%" precision={1} />
              </Col>
              <Col span={8}>
                <Statistic title="Reste à vivre" value={scoring.details.reste_a_vivre} suffix=" FCFA" />
              </Col>
              <Col span={8}>
                <Statistic title="Ancienneté" value={scoring.details.anciennete_mois} suffix=" mois" />
              </Col>
              <Col span={8}>
                <Statistic title="Régularité" value={scoring.details.regularite_revenus} precision={2} />
              </Col>
              <Col span={8}>
                <Statistic title="Épargne totale" value={scoring.details.epargne_totale} suffix=" FCFA" />
              </Col>
              <Col span={8}>
                <Statistic title="Cohérence" value={scoring.details.coherence_globale} precision={2} />
              </Col>
            </Row>
          </Panel>
        </Collapse>
      </div>
    );
  };

  /**
   * Rendu de la simulation
   */
  const renderSimulation = () => {
    if (!simulation) {
      return (
        <div>
          <Alert
            message="Aucune simulation"
            description="Cliquez sur les recommandations pour simuler leur impact"
            type="info"
          />
          <Button
            icon={<ExperimentOutlined />}
            onClick={() => performSimulation({ revenu_mensuel: 500000 })}
            style={{ marginTop: 16 }}
          >
            Tester une simulation exemple
          </Button>
        </div>
      );
    }

    return (
      <div className="simulation-results">
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card title="Score Actuel">
              <Statistic
                value={simulation.current_score.global}
                suffix="/100"
                precision={1}
                valueStyle={{ color: getRiskColor(simulation.current_score.niveau_risque) }}
              />
              <Tag color={getRiskColor(simulation.current_score.niveau_risque)}>
                {simulation.current_score.niveau_risque}
              </Tag>
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Score Simulé">
              <Statistic
                value={simulation.simulated_score.global}
                suffix="/100"
                precision={1}
                valueStyle={{ color: getRiskColor(simulation.simulated_score.niveau_risque) }}
              />
              <Tag color={getRiskColor(simulation.simulated_score.niveau_risque)}>
                {simulation.simulated_score.niveau_risque}
              </Tag>
            </Card>
          </Col>
        </Row>

        <Card title="Amélioration" style={{ marginTop: 16 }}>
          <Statistic
            title="Gain de points"
            value={simulation.improvement.points}
            precision={1}
            prefix={simulation.improvement.points > 0 ? '+' : ''}
            valueStyle={{ color: simulation.improvement.points > 0 ? '#52c41a' : '#f5222d' }}
          />
          <Statistic
            title="Pourcentage"
            value={simulation.improvement.percentage}
            suffix="%"
            precision={1}
            style={{ marginTop: 16 }}
          />
          {simulation.improvement.risk_level_changed && (
            <Alert
              message="Changement de niveau de risque"
              type="success"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </Card>
      </div>
    );
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>Scoring V2.0 - Système Unifié</span>
          <Tag color="blue">v2.0</Tag>
        </div>
      }
      extra={
        <div>
          <Switch
            checked={useEnrichment}
            onChange={setUseEnrichment}
            checkedChildren="Enrichissement ON"
            unCheckedChildren="Enrichissement OFF"
            style={{ marginRight: 8 }}
          />
          <Button
            type="primary"
            icon={<SyncOutlined spin={loading} />}
            onClick={() => performScoring(false)}
            loading={loading}
          >
            Calculer Score
          </Button>
        </div>
      }
    >
      <Spin spinning={loading}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane
            tab={
              <span>
                <BarChartOutlined />
                Vue d'ensemble
              </span>
            }
            key="overview"
          >
            {scoringResult ? renderScoreOverview() : (
              <Alert
                message="Aucun score calculé"
                description="Cliquez sur 'Calculer Score' pour lancer le scoring v2.0"
                type="info"
                showIcon
              />
            )}
          </TabPane>

          <TabPane
            tab={
              <span>
                <CheckCircleOutlined />
                Détails
              </span>
            }
            key="details"
          >
            {scoringResult ? renderDetails() : (
              <Alert message="Calculez d'abord un score" type="warning" />
            )}
          </TabPane>

          <TabPane
            tab={
              <span>
                <ExperimentOutlined />
                Simulation
              </span>
            }
            key="simulation"
          >
            {renderSimulation()}
          </TabPane>

          <TabPane
            tab={
              <span>
                <HistoryOutlined />
                Historique
              </span>
            }
            key="history"
          >
            <Button onClick={loadHistory} loading={loading}>
              Charger l'historique
            </Button>
            {history && (
              <Timeline style={{ marginTop: 16 }}>
                {history.scores.map((score) => (
                  <Timeline.Item key={score.id} color={getRiskColor(score.niveau_risque as RiskLevel)}>
                    <p>
                      Score: {score.score}/100 - {score.niveau_risque}
                      {score.with_enrichment && <Tag color="blue">Enrichi</Tag>}
                    </p>
                    <p style={{ color: '#8c8c8c', fontSize: 12 }}>
                      {new Date(score.timestamp).toLocaleString()} par {score.user}
                    </p>
                  </Timeline.Item>
                ))}
              </Timeline>
            )}
          </TabPane>
        </Tabs>
      </Spin>
    </Card>
  );
};

export default ScoringV2Display;
